import axios from "axios";
import { getApiUrl as getDomainApiUrl } from "../utils/domainConfig";
import {
  createTurnstileRequestInterceptor,
  createTurnstileTokenInvalidationInterceptor,
  getTurnstileToken,
  getTurnstileBridge,
} from "../hooks/useTurnstile";
import { createTurnstileCookieManager } from "../hooks/turnstileCookieManager";
import type { TurnstileCookieManager } from "../hooks/turnstileCookieManager";

// Re-export getApiUrl from domainConfig for backward compatibility
export const getApiUrl = (): string => {
  const url = getDomainApiUrl();
  // Remove trailing slash to prevent double slash issues
  return url.endsWith("/") ? url.slice(0, -1) : url;
};

const apiClient = axios.create({
  // Don't set baseURL here - it will be set dynamically in the request interceptor
  withCredentials: true, // Required for cross-origin cookies (Turnstile cookie flow)
});

apiClient.interceptors.request.use(
  (config) => {
    // Dynamically set the baseURL for each request
    const baseURL = getApiUrl();
    config.baseURL = baseURL;

    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add original host header for hotel resolution
    const hostname = window.location.hostname;
    if (hostname === "localhost" || hostname === "127.0.0.1") {
      // For development, use mock host from localStorage or default
      const mockHost = localStorage.getItem("mockHost") || "localhost";
      config.headers["X-Original-Host"] = mockHost;
    } else {
      // For production, use actual hostname
      config.headers["X-Original-Host"] = hostname;
    }

    // Add tenant selection header (NEW - for multi-tenant users)
    const selectedTenantId = localStorage.getItem("selectedTenantId");
    if (selectedTenantId) {
      config.headers["X-Selected-Tenant-Id"] = selectedTenantId;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Legacy Turnstile interceptor: attaches cf-turnstile-response header to /grants/calls requests.
// This is the per-request fallback during the migration period.
// The TurnstileCookieGuard on backend checks cookie first, falls back to this header.
apiClient.interceptors.request.use(
  createTurnstileRequestInterceptor(getTurnstileToken)
);

// ============================================================
// Cookie-based Turnstile flow
// ============================================================

// Create cookie manager and connect it to the TurnstileProvider bridge
const bridge = getTurnstileBridge();

const cookieManager: TurnstileCookieManager = createTurnstileCookieManager({
  apiClient,
  resetWidget: () => bridge.resetWidget(),
  onCookieReady: (ready: boolean) => {
    if (bridge.onCookieReady) {
      bridge.onCookieReady(ready);
    }
  },
  onError: () => {
    if (bridge.onError) {
      bridge.onError();
    }
  },
  forceRemount: () => bridge.forceRemount(),
});

// Connect the cookie manager's handleNewToken to the bridge so
// TurnstileProvider.handleSuccess can trigger cookie verification
bridge.handleNewToken = (token: string) => cookieManager.handleNewToken(token);

// Expose lastVerifyTimestamp via bridge for visibilitychange handler
bridge.getLastVerifyTimestamp = () => cookieManager.getLastVerifyTimestamp();

// Register the cookie-based response interceptor (handles 403 retry queue)
// Success path: invalidate Turnstile token after each successful protected
// request to prevent re-use of a single-use Cloudflare token (e.g., user
// goes back in a multi-step wizard and re-submits → 403 token consumed).
// Error path: existing cookie manager retry queue for 403 TURNSTILE_*.
const cookieResponseInterceptor = cookieManager.createResponseInterceptor();
const tokenInvalidationInterceptor = createTurnstileTokenInvalidationInterceptor(
  () => bridge.invalidateToken(),
  () => bridge.resetWidget(),
);
apiClient.interceptors.response.use(
  tokenInvalidationInterceptor,
  cookieResponseInterceptor
);

// Export the cookie manager for use by components (e.g., checking isCookieReady)
export { cookieManager };

export default apiClient;
